import { memo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingFallback from '../components/elements/LoadingFallback';
import { useSelector, useDispatch } from 'react-redux';
import { loadUserData, determineUserDestination } from '../util/navigationUtils';
import { io } from 'socket.io-client';
import { 
  setCoverImages,
  setImages,
} from '../redux/slice/sketchSlice';
import { 
  setCoverImageUploadStatus,
  setImageBatchUploadStatus, // 🆕 Use dedicated batch upload action
  clearUploadingImages,
  setIsComingFromNonUserRoute
} from '../redux/slice/stepSlice';
import { fetchBatchImagesByBookId } from '../services/mediaService';
import { updateBook } from '../redux/slice/profileSlice';
import { SOCKET_URL } from '../config/apiConfig';

const UserRoute = memo(function UserRoute({ children }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { session } = useSelector((state) => state?.authProvider);
  const { booksLoading = false } = useSelector((state) => state?.profile);
  const { isComingFromNonUserRoute } = useSelector((state) => state?.stepFlow);
  const { activeBookId } = useSelector((state) => state?.stepFlow);
  const coverImages = useSelector((state) => state.sketch.coverImages);
  const images = useSelector((state) => state.sketch.images);
  const [hasRedirected, setHasRedirected] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [userData, setUserData] = useState(null);

  // Handle authentication redirects
  useEffect(() => {
    if (session?.status === 'unauthorized') {
      navigate('/signin');
      return;
    }

    if (session?.status === 'authorized' && session?.user?.role === 'admin') {
      navigate('/admin');
      return;
    }
  }, [session?.status, session?.user?.role, navigate]);

  // Load user data when authorized
  useEffect(() => {
    const fetchUserData = async () => {
      if (
        session?.status === 'authorized' && 
        session?.user?.role === 'user' &&
        session?.user?._id &&
        !isLoadingData &&
        !userData
      ) {
        setIsLoadingData(true);
        try {
          const data = await loadUserData(
            session.user._id, 
            dispatch, 
            isComingFromNonUserRoute
          );
          setUserData(data);
        } catch (error) {
          console.error('Error loading user data:', error);
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    fetchUserData();
  }, [session?.user?._id, isComingFromNonUserRoute, isLoadingData, userData?._id]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const stepCode = searchParams.get('stepCode');

    if (window.location.pathname === '/thank-you' || !stepCode) {
      setHasRedirected(true);
    }
  }, [window.location.pathname]);

  // Perform smart redirect once data is loaded
  useEffect(() => {
    if (
      session?.status === 'authorized' &&
      session?.user?.role === 'user' &&
      userData &&
      !hasRedirected &&
      !booksLoading
    ) {
      const destination = determineUserDestination(
        userData.profile, 
        userData.myBooks, 
        userData.activeBookId
      );

      console.log('UserRoute redirecting to:', destination);
      navigate(destination, { replace: true });
      setHasRedirected(true); // Reset the state
      dispatch(setIsComingFromNonUserRoute(false)); // Reset the state
    }
  }, [
    session?.user?._id, 
    userData?._id, 
    hasRedirected, 
    booksLoading, 
    isComingFromNonUserRoute
  ]);

  // Socket management with RunPod event listeners
  useEffect(() => {
    let socket;

    if (session?.user?._id && session?.user?.role === "user") {
      const token = localStorage.getItem('auth_token') || session?.user?.accessToken;
      
      if (!token) {
        console.error('No token found for socket authentication');
        return;
      }

      socket = io(SOCKET_URL, { auth: { token } });

      // Connection events
      socket.on('connect', () => {
        console.log('🔌 Socket connected in UserRoute:', socket.id);
      });

      socket.on('disconnect', () => {
        console.log('🔌 Socket disconnected from UserRoute');
      });

      socket.on('connect_error', (error) => {
        console.error('❌ Socket connection error:', error.message);
      });

      // ========================================
      // 🆕 COVER WORKFLOW EVENTS (with title)
      // ========================================

      socket.on('runpod:cover:started', (data) => {
        console.log('🚀 Cover job started:', data);
        
        if (data.bookId === activeBookId) {
          dispatch(setCoverImageUploadStatus(true));
        }
      });

      socket.on('runpod:cover:progress', (data) => {
        console.log('📊 Cover progress:', data);
        
        if (data.bookId === activeBookId) {
          dispatch(setCoverImageUploadStatus(true));
          const { currentStage, overallPercentage } = data;
          console.log(`Cover Progress: ${overallPercentage}% - ${currentStage}`);
        }
      });

      socket.on('runpod:cover:completed', (data) => {
        console.log('✅ Cover job completed:', data);
        
        if (data.bookId === activeBookId) {
          fetchBatchImagesByBookId(activeBookId).then(result => {
            const coverImageData = result?.docs?.data?.filter(
              (image) => !!image.isCover
            ) || [];

            if (!!coverImageData[0]?.AIUrl) {
              dispatch(
                updateBook({
                  _id: activeBookId,
                  coverImage: coverImageData[0].AIUrl
                })
              )
            }

            dispatch(setCoverImages(coverImageData));
          }).finally(() => {
            dispatch(setCoverImageUploadStatus(false));
          });
        }
      });

      socket.on('runpod:cover:failed', (data) => {
        console.error('❌ Cover job failed:', data);
        
        if (data.bookId === activeBookId) {
          dispatch(setCoverImageUploadStatus(false));
        }
      });

      socket.on('runpod:cover:error', (data) => {
        console.error('❌ Cover error:', data);
        
        if (data.bookId === activeBookId) {
          dispatch(setCoverImageUploadStatus(false));
        }
      });

      socket.on('runpod:cover:timeout', (data) => {
        console.error('⏱️ Cover job timeout:', data);
        
        if (data.bookId === activeBookId) {
          dispatch(setCoverImageUploadStatus(false));
        }
      });

      // ========================================
      // 🆕 LINEART WORKFLOW EVENTS (no title)
      // ========================================

      socket.on('runpod:lineart:started', (data) => {
        console.log('🚀 Lineart job started:', data);
        
        if (data.bookId === activeBookId) {
          dispatch(setImageBatchUploadStatus(true)); // 🔧 FIXED
        }
      });

      socket.on('runpod:lineart:progress', (data) => {
        console.log('📊 Lineart progress:', data);
        
        if (data.bookId === activeBookId) {
          dispatch(setImageBatchUploadStatus(true)); // 🔧 FIXED
          const { currentStage, overallPercentage, currentImage, totalImages } = data;
          console.log(`Lineart Progress: ${overallPercentage}% - ${currentStage} (${currentImage}/${totalImages})`);
        }
      });

      socket.on('runpod:lineart:completed', (data) => {
        console.log('✅ Lineart job completed:', data);
        
        if (data.bookId === activeBookId) {
          fetchBatchImagesByBookId(activeBookId).then(result => {
            const imageData = result?.docs?.data?.filter(
              (image) => !image.isCover
            ) || [];

            dispatch(setImages(imageData));
          }).finally(() => {
            dispatch(setImageBatchUploadStatus(false)); // 🔧 FIXED
            dispatch(clearUploadingImages());
          });
        }
      });

      socket.on('runpod:lineart:failed', (data) => {
        console.error('❌ Lineart job failed:', data);
        
        if (data.bookId === activeBookId) {
          dispatch(setImageBatchUploadStatus(false)); // 🔧 FIXED
          dispatch(clearUploadingImages());
        }
      });

      socket.on('runpod:lineart:error', (data) => {
        console.error('❌ Lineart error:', data);
        
        if (data.bookId === activeBookId) {
          dispatch(setImageBatchUploadStatus(false)); // 🔧 FIXED
          dispatch(clearUploadingImages());
        }
      });

      socket.on('runpod:lineart:timeout', (data) => {
        console.error('⏱️ Lineart job timeout:', data);
        
        if (data.bookId === activeBookId) {
          dispatch(setImageBatchUploadStatus(false)); // 🔧 FIXED
          dispatch(clearUploadingImages());
        }
      });

      // ========================================
      // LEGACY GENERIC EVENTS (backward compatibility)
      // ========================================

      socket.on('runpod:started', (data) => {
        console.log('🚀 RunPod job started (generic):', data);
        
        if (data.bookId === activeBookId) {
          if (data.workflowType === 'cover') {
            dispatch(setCoverImageUploadStatus(true));
          } else {
            dispatch(setImageBatchUploadStatus(true)); // 🔧 FIXED
          }
        }
      });

      socket.on('runpod:progress', (data) => {
        console.log('📊 RunPod progress (generic):', data);
        
        if (data.bookId === activeBookId) {
          if (data.workflowType === 'cover') {
            dispatch(setCoverImageUploadStatus(true));
          } else {
            dispatch(setImageBatchUploadStatus(true)); // 🔧 FIXED
          }
        }
      });

      socket.on('runpod:completed', (data) => {
        console.log('✅ RunPod job completed (generic):', data);
        
        if (data.bookId === activeBookId) {
          fetchBatchImagesByBookId(activeBookId).then(result => {
            if (data.workflowType === 'cover') {
              const coverImageData = result?.docs?.data?.filter(
                (image) => !!image.isCover
              ) || [];
              dispatch(setCoverImages(coverImageData));
              dispatch(setCoverImageUploadStatus(false));
            } else {
              const imageData = result?.docs?.data?.filter(
                (image) => !image.isCover
              ) || [];
              dispatch(setImages(imageData));
              dispatch(setImageBatchUploadStatus(false)); // 🔧 FIXED
              dispatch(clearUploadingImages());
            }
          });
        }
      });

      socket.on('runpod:failed', (data) => {
        console.error('❌ RunPod job failed (generic):', data);
        
        if (data.bookId === activeBookId) {
          if (data.workflowType === 'cover') {
            dispatch(setCoverImageUploadStatus(false));
          } else {
            dispatch(setImageBatchUploadStatus(false)); // 🔧 FIXED
            dispatch(clearUploadingImages());
          }
        }
      });

      socket.on('runpod:error', (data) => {
        console.error('❌ RunPod error (generic):', data);
        
        if (data.bookId === activeBookId) {
          if (data.workflowType === 'cover') {
            dispatch(setCoverImageUploadStatus(false));
          } else {
            dispatch(setImageBatchUploadStatus(false)); // 🔧 FIXED
            dispatch(clearUploadingImages());
          }
        }
      });

      socket.on('runpod:timeout', (data) => {
        console.error('⏱️ RunPod job timeout (generic):', data);
        
        if (data.bookId === activeBookId) {
          if (data.workflowType === 'cover') {
            dispatch(setCoverImageUploadStatus(false));
          } else {
            dispatch(setImageBatchUploadStatus(false)); // 🔧 FIXED
            dispatch(clearUploadingImages());
          }
        }
      });
    }

    return () => {
      if (socket) {
        // Remove all cover workflow listeners
        socket.off('runpod:cover:started');
        socket.off('runpod:cover:progress');
        socket.off('runpod:cover:completed');
        socket.off('runpod:cover:failed');
        socket.off('runpod:cover:error');
        socket.off('runpod:cover:timeout');

        // Remove all lineart workflow listeners
        socket.off('runpod:lineart:started');
        socket.off('runpod:lineart:progress');
        socket.off('runpod:lineart:completed');
        socket.off('runpod:lineart:failed');
        socket.off('runpod:lineart:error');
        socket.off('runpod:lineart:timeout');

        // Remove generic listeners
        socket.off('runpod:started');
        socket.off('runpod:progress');
        socket.off('runpod:completed');
        socket.off('runpod:failed');
        socket.off('runpod:error');
        socket.off('runpod:timeout');
        
        socket.disconnect();
        console.log('🧹 Socket cleanup in UserRoute');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?._id, session?.user?.role, activeBookId, coverImages, images, dispatch]);

  // Show loading state
  if (session?.status === 'loading' || isLoadingData || booksLoading) {
    return <LoadingFallback />;
  }

  // Only render children if authorized user
  return session?.status === 'authorized' && session?.user?.role === 'user'
    ? children
    : null;
});

export default UserRoute;